import BannerComp from "../components/BannerComp";
import Seo from "../components/Seo";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { createBreadcrumbSchema, createWebPageSchema } from "../lib/seo";
import styles from "./TermsConditions.module.css";

const sections = [
  {
    title: "Using Our Services",
    content:
      "We support your healthcare operations with reliable billing and administrative solutions. By using our services, you agree to act legally and respectfully. Accessing our platform without permission may result in service suspension.",
  },
  {
    title: "Creating an Account",
    content:
      "Some features require you to create an account. Keep your login details safe and private. You are responsible for all activity on your account. If you think it has been compromised, contact us right away.",
  },
  {
    title: "What We Offer",
    content:
      "PSP focuses on medical billing services. These include coding, credentialing, denial management, accounts receivable follow-up, and virtual scribe solutions. Your service contract or subscription plan will outline the services and delivery times.",
  },
  {
    title: "Payments and Billing",
    content:
      "Our services are offered through flexible billing plans-monthly or annually. All fees are non-refundable unless specifically stated in a written agreement. We will always make our pricing and terms clear before you commit.",
  },
  {
    title: "Intellectual Property",
    content:
      "All content on psprcm.us, including text, branding, graphics, and technology, belongs to PSP. Please do not reproduce or reuse our materials without permission.",
  },
  {
    title: "Partner Integrations",
    content:
      "We might team up with trusted third-party platforms to provide additional tools and services. We focus on reliability, but we are not responsible for the content or performance of third-party services.",
  },
  {
    title: "SMS Communication Policy",
    list: [
      "Appointment confirmations and reminders",
      "Service updates or alerts",
      "Promotional offers or campaigns",
      "Account verification codes (2FA)",
    ],
    footer:
      "Message frequency may vary depending on your preferences and usage. Standard messaging rates apply. You can opt out anytime by replying STOP to a message, or get help by replying HELP or contacting us at info@psprcm.us.",
  },
  {
    title: "Service Suspension or Termination",
    content:
      "We can suspend or end your access if you violate these terms. We will try to give notice whenever we can.",
  },
  {
    title: "No Guarantees (Disclaimer)",
    content:
      'While we work hard to provide dependable service, all offerings are provided "as is." We cannot make guarantees or warranties, expressed or implied.',
  },
  {
    title: "Limitation of Liability",
    content:
      "PSP will not be held responsible for any indirect, incidental, or consequential damages resulting from your use, or inability to use, our services.",
  },
  {
    title: "Governing Law",
    content:
      "These Terms follow the laws of the State of Nevada. Any disputes will be resolved under Nevada's legal rules.",
  },
  {
    title: "Text Message Disclaimer",
    content:
      "We use SMS to keep you informed and updated. Your carrier affects message delivery. We are not responsible for delays or messages that do not arrive.",
  },
  {
    title: "Data Privacy",
    content:
      "We will never share or sell your mobile information to third parties for marketing. We respect your privacy and protect your opt-in data as stated in our Privacy Policy.",
  },
  {
    title: "Opting Out",
    content:
      "Do not want to receive text messages? No problem. You can opt out at any time by replying STOP to any message or emailing us at info@psprcm.us. You might still receive important service messages from other departments or numbers.",
  },
];

const seo = {
  title: "Terms and Conditions | PSP RCM",
  description:
    "Review the PSP RCM terms and conditions for website use, responsibilities, and service-related terms.",
  keywords: "terms and conditions, PSP RCM terms, website terms",
  schema: [
    createWebPageSchema({
      path: "/terms-and-conditions",
      title: "Terms and Conditions",
      description:
        "Review the PSP RCM terms and conditions for website use, responsibilities, and service-related terms.",
    }),
    createBreadcrumbSchema("/terms-and-conditions", "Terms and Conditions"),
  ],
};

export default function TermsConditions() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/terms-and-conditions"
        schema={seo.schema}
      />
      <BannerComp
        heading="Terms & Conditions"
        paragraph="Please review these terms carefully before using our services. They explain service use, account responsibility, payment expectations, messaging, and your relationship with PSP."
        BannerBgImg="/banner-bg.webp"
        centered={true}
        form={false}
      />

      <section className={styles.page}>
        <div className={`container ${styles.shell}`}>
          <div className={styles.intro}>
            <p>
              Welcome to PSP. By using our website and services, you agree to
              the terms below. If you do not agree with these terms, please do
              not use our services.
            </p>
          </div>

          <div className={styles.policyContent}>
            {sections.map((section) => (
              <section key={section.title} className={styles.policySection}>
                <h2>{section.title}</h2>
                {section.content ? <p>{section.content}</p> : null}
                {section.list ? (
                  <ul className={styles.list}>
                    {section.list.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
                {section.footer ? <p>{section.footer}</p> : null}
              </section>
            ))}

            <section className={styles.policySection}>
              <h2>Have Questions?</h2>
              <p>
                We are here to help. If you have any questions or need
                assistance, reach out to us using the details below.
              </p>
              <div className={styles.contactLinks}>
                <a href="tel:+16155536787" className={styles.contactItem}>
                  <FaPhoneAlt />
                  <span>(615) 553-6787</span>
                </a>
                <a href="mailto:info@psprcm.us" className={styles.contactItem}>
                  <FaEnvelope />
                  <span>info@psprcm.us</span>
                </a>
              </div>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}
